import { useMemo, useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, SlidersHorizontal, X, LayoutGrid, List } from "lucide-react";
import { CATEGORIES, PRODUCTS, catBySlug, productsByCat, saleProducts, newProducts, searchProducts, fmt } from "../data/store";
import { ProductCard } from "../components/ProductCard";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";

const SORTS = [
  { v: "pop", l: "Popularitāte" },
  { v: "priceAsc", l: "Cena: augoša" },
  { v: "priceDesc", l: "Cena: dilstoša" },
  { v: "new", l: "Jaunākie" },
  { v: "name", l: "Nosaukums A–Z" },
];

const Skeleton = () => (
  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="border border-[#E2E2E2] rounded-md overflow-hidden animate-pulse">
        <div className="aspect-square bg-[#F5F5F5]" />
        <div className="p-4 space-y-2">
          <div className="h-3 bg-[#F5F5F5] rounded w-1/3" />
          <div className="h-4 bg-[#F5F5F5] rounded" />
          <div className="h-5 bg-[#F5F5F5] rounded w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

const FilterGroup = ({ title, children }) => (
  <div className="border-b border-[#E2E2E2] pb-4 mb-4">
    <h4 className="text-sm font-bold mb-3">{title}</h4>
    {children}
  </div>
);

export default function CategoryPage({ mode }) {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const sub = params.get("sub");
  const q = params.get("q") || "";

  const cat = mode ? null : catBySlug(slug);
  const base = useMemo(() => {
    if (mode === "sale") return saleProducts();
    if (mode === "new") return newProducts();
    if (mode === "search") return searchProducts(q);
    let list = productsByCat(slug);
    if (sub) list = list.filter((x) => x.subcategory === sub);
    return list;
  }, [mode, slug, sub, q]);

  const maxPrice = Math.max(10, ...base.map((x) => Math.ceil(x.price)));
  const [price, setPrice] = useState([0, maxPrice]);
  const [brands, setBrands] = useState([]);
  const [inStock, setInStock] = useState(false);
  const [saleOnly, setSaleOnly] = useState(false);
  const [sort, setSort] = useState("pop");
  const [view, setView] = useState("grid");
  const [mobileFilters, setMobileFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(12);

  useEffect(() => {
    setPrice([0, maxPrice]); setBrands([]); setInStock(false); setSaleOnly(false); setVisible(12);
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, sub, q, mode]);

  const brandCounts = useMemo(() => {
    const m = {};
    base.forEach((x) => { m[x.brand] = (m[x.brand] || 0) + 1; });
    return Object.entries(m).sort();
  }, [base]);

  const filtered = useMemo(() => {
    let list = base.filter((x) => x.price >= price[0] && x.price <= price[1]);
    if (brands.length) list = list.filter((x) => brands.includes(x.brand));
    if (inStock) list = list.filter((x) => x.stock);
    if (saleOnly) list = list.filter((x) => x.oldPrice);
    const s = [...list];
    if (sort === "priceAsc") s.sort((a, b) => a.price - b.price);
    else if (sort === "priceDesc") s.sort((a, b) => b.price - a.price);
    else if (sort === "new") s.sort((a, b) => Number(b.isNew) - Number(a.isNew));
    else if (sort === "name") s.sort((a, b) => a.name.localeCompare(b.name, "lv"));
    else s.sort((a, b) => b.rating - a.rating);
    return s;
  }, [base, price, brands, inStock, saleOnly, sort]);

  const title = mode === "sale" ? "Akcijas" : mode === "new" ? "Jaunumi" : mode === "search" ? `Meklēšanas rezultāti: “${q}”` : sub || cat?.name || "Kategorija";
  const chips = [
    ...brands.map((b) => ({ label: b, clear: () => setBrands((p) => p.filter((x) => x !== b)) })),
    ...(inStock ? [{ label: "Noliktavā", clear: () => setInStock(false) }] : []),
    ...(saleOnly ? [{ label: "Tikai akcijas", clear: () => setSaleOnly(false) }] : []),
    ...(price[0] > 0 || price[1] < maxPrice ? [{ label: `${fmt(price[0])} – ${fmt(price[1])}`, clear: () => setPrice([0, maxPrice]) }] : []),
  ];

  const Filters = () => (
    <div data-testid="filters-panel">
      <FilterGroup title="Cena (€, ar PVN)">
        <Slider value={price} min={0} max={maxPrice} step={0.5} onValueChange={setPrice} data-testid="price-slider" />
        <div className="flex justify-between text-xs text-[#6B6B6B] mt-2"><span>{fmt(price[0])}</span><span>{fmt(price[1])}</span></div>
      </FilterGroup>
      <FilterGroup title="Zīmols">
        <div className="space-y-2">
          {brandCounts.map(([b, n]) => (
            <label key={b} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={brands.includes(b)} onCheckedChange={(c) => setBrands((p) => (c ? [...p, b] : p.filter((x) => x !== b)))} data-testid={`brand-filter-${b.replace(/\s/g, "-")}`} />
              <span className="flex-1">{b}</span>
              <span className="text-xs text-[#6B6B6B]">({n})</span>
            </label>
          ))}
        </div>
      </FilterGroup>
      <FilterGroup title="Pieejamība">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={inStock} onCheckedChange={setInStock} data-testid="instock-filter" />
          <span className="flex-1">Tikai noliktavā</span>
          <span className="text-xs text-[#6B6B6B]">({base.filter((x) => x.stock).length})</span>
        </label>
      </FilterGroup>
      <FilterGroup title="Akcijas">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={saleOnly} onCheckedChange={setSaleOnly} data-testid="sale-filter" />
          <span className="flex-1">Tikai akcijas preces</span>
          <span className="text-xs text-[#6B6B6B]">({base.filter((x) => x.oldPrice).length})</span>
        </label>
      </FilterGroup>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 mt-6">
      <nav className="flex items-center gap-1.5 text-xs text-[#6B6B6B] mb-4 flex-wrap" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#111111] hover:underline">Sākums</Link>
        <ChevronRight size={12} />
        {cat ? (
          <>
            <Link to={`/kategorija/${cat.slug}`} className="hover:text-[#111111] hover:underline">{cat.name}</Link>
            {sub && <><ChevronRight size={12} /><span className="text-[#111111] font-semibold">{sub}</span></>}
          </>
        ) : (
          <span className="text-[#111111] font-semibold">{title}</span>
        )}
      </nav>

      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight uppercase" data-testid="plp-title">{title} <span className="text-lg font-semibold text-[#6B6B6B]">({filtered.length} preces)</span></h1>
        {cat && !sub && <p className="text-sm text-[#6B6B6B] mt-1 max-w-2xl">{cat.desc}</p>}
        {cat && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {cat.subs.map((s) => (
              <button key={s} onClick={() => setParams(s === sub ? {} : { sub: s })} data-testid={`subcat-chip-${s.replace(/[\s,]+/g, "-")}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-none border transition-colors ${s === sub ? "bg-[#111111] text-white border-[#111111]" : "border-[#E2E2E2] hover:border-[#111111] hover:text-[#111111]"}`}>{s}</button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <aside className="hidden lg:block">
          <Filters />
        </aside>
        <div className="lg:col-span-3">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <button onClick={() => setMobileFilters(true)} data-testid="mobile-filters-btn"
              className="lg:hidden flex items-center gap-2 text-sm font-semibold border border-[#E2E2E2] rounded-md px-3 py-2 hover:border-[#111111]">
              <SlidersHorizontal size={15} /> Filtri {chips.length > 0 && `(${chips.length})`}
            </button>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
              className="text-sm border border-[#E2E2E2] rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#111111]">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <div className="hidden sm:flex border border-[#E2E2E2] rounded-md overflow-hidden ml-auto">
              <button onClick={() => setView("grid")} data-testid="view-grid-btn" aria-label="Režģis" className={`p-2 ${view === "grid" ? "bg-[#111111] text-white" : "hover:bg-[#F5F5F5]"}`}><LayoutGrid size={16} /></button>
              <button onClick={() => setView("list")} data-testid="view-list-btn" aria-label="Saraksts" className={`p-2 ${view === "list" ? "bg-[#111111] text-white" : "hover:bg-[#F5F5F5]"}`}><List size={16} /></button>
            </div>
          </div>

          {chips.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-4" data-testid="active-filter-chips">
              {chips.map((c, i) => (
                <button key={i} onClick={c.clear} className="flex items-center gap-1 text-xs font-semibold bg-[#F5F5F5] border border-[#E2E2E2] rounded-none px-3 py-1.5 hover:border-[#D0121A] hover:text-[#D0121A] transition-colors">
                  {c.label} <X size={12} />
                </button>
              ))}
            </div>
          )}

          {loading ? <Skeleton /> : filtered.length === 0 ? (
            <div className="text-center py-16" data-testid="no-results">
              <p className="text-lg font-bold">Nekas netika atrasts</p>
              <p className="text-sm text-[#6B6B6B] mt-1">Pamēģini mainīt filtrus vai meklēt ko citu.</p>
              <div className="flex gap-2 justify-center mt-4 flex-wrap">
                {CATEGORIES.slice(0, 4).map((c) => (
                  <Link key={c.slug} to={`/kategorija/${c.slug}`} className="text-xs font-semibold border border-[#E2E2E2] rounded-none px-3 py-1.5 hover:border-[#111111] hover:text-[#111111]">{c.name}</Link>
                ))}
              </div>
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="product-grid">
              {filtered.slice(0, visible).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="space-y-3" data-testid="product-list">
              {filtered.slice(0, visible).map((p) => (
                <Link key={p.id} to={`/prece/${p.id}`} className="flex gap-4 border border-[#E2E2E2] rounded-md p-4 hover:border-[#111111] hover:shadow-md transition-all bg-white">
                  <img src={p.image} alt={p.name} loading="lazy" className="w-24 h-24 object-cover rounded bg-[#F5F5F5]" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-[#6B6B6B]">{p.brand}</span>
                    <h3 className="text-sm font-semibold">{p.name}</h3>
                    <p className="text-xs text-[#6B6B6B] mt-1 line-clamp-2">{p.description}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-bold">{fmt(p.price)}</div>
                    <div className="text-[11px] text-[#6B6B6B]">ar PVN</div>
                    {p.packQty > 1 && <div className="text-xs text-[#6B6B6B] mt-1">Iep. {p.packQty} gab.</div>}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && visible < filtered.length && (
            <div className="text-center mt-8">
              <button onClick={() => setVisible((v) => v + 12)} data-testid="load-more-btn"
                className="border-2 border-[#E2E2E2] hover:border-[#111111] hover:text-[#111111] font-semibold px-8 py-3 rounded-md transition-colors">Ielādēt vairāk</button>
            </div>
          )}
        </div>
      </div>

      {mobileFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFilters(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white overflow-y-auto p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold">Filtri</h3>
              <button onClick={() => setMobileFilters(false)} data-testid="mobile-filters-close" aria-label="Aizvērt"><X size={20} /></button>
            </div>
            <Filters />
            <button onClick={() => setMobileFilters(false)} className="w-full bg-[#111111] text-white font-bold py-3 rounded-md mt-2">Rādīt {filtered.length} preces</button>
          </div>
        </div>
      )}
    </div>
  );
}
